import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { getLscLiveSubscriptions } from '../api';
import type {
  LscLiveReadinessReport,
  LscLiveSubscription,
  LscLiveSubscriptionSyncResult,
} from '../types';
import LiveDetectionSyncModal from './LiveDetectionSyncModal';
import {
  formatSyncResultMessage,
  latestLiveDetectionSyncAt,
} from './liveDetectionSyncUtils';

export type LiveDetectionPanelProps = {
  leagueId: string;
  readiness: LscLiveReadinessReport | null;
  readinessLoading: boolean;
  onRefreshReadiness: () => Promise<void> | (() => void);
  canRepairAlerts?: boolean;
  onMutationSuccess?: () => void;
};

function formatSyncTimestamp(value?: string | null): string {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

export function LiveDetectionPanelView({
  readiness,
  subscriptions,
  loading,
  error,
  syncMessage,
  syncPartialFailure,
  lastResult,
  onRefresh,
  onOpenSync,
  onRetrySync,
  canRepairAlerts = true,
}: {
  readiness: LscLiveReadinessReport | null;
  subscriptions: LscLiveSubscription[];
  loading: boolean;
  error: string | null;
  syncMessage: string | null;
  syncPartialFailure: boolean;
  lastResult: LscLiveSubscriptionSyncResult | null;
  onRefresh: () => void;
  onOpenSync: () => void;
  onRetrySync: () => void;
  canRepairAlerts?: boolean;
}) {
  const stats = readiness?.eventsub_subscription_status;
  const lastSyncedAt = latestLiveDetectionSyncAt(subscriptions);
  const liveDetectionState = stats?.live_detection_state;
  const hasFailedAlerts = Boolean(stats && stats.members_failed > 0);
  const isProvisioning =
    liveDetectionState === 'provisioning' ||
    Boolean(
      stats &&
        !hasFailedAlerts &&
        (stats.members_pending_verification > 0 ||
          stats.members_missing_online > 0 ||
          stats.members_missing_offline > 0)
    );
  const needsRepair = hasFailedAlerts;
  const isReady =
    liveDetectionState === 'ready' ||
    Boolean(
      stats &&
        !needsRepair &&
        !isProvisioning &&
        stats.members_missing_online === 0 &&
        stats.members_missing_offline === 0 &&
        stats.members_pending_verification === 0 &&
        stats.members_failed === 0 &&
        stats.active_members_with_twitch > 0
    );

  return (
    <section
      id="live-detection"
      className="border-t border-secondary-700/60 pt-4 space-y-4 scroll-mt-24"
      data-testid="live-detection-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-white">Twitch Live Detection</h3>
          <p className="text-sm text-secondary-400 mt-1">
            Live detection is automatic for connected members. Games that need a closer look show
            up in Needs Attention — no button press required.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="btn-secondary inline-flex items-center gap-2 text-sm shrink-0"
          aria-label="Refresh live detection status"
          data-testid="live-detection-refresh"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-400" data-testid="live-detection-loading">
          Loading live detection status...
        </p>
      ) : error ? (
        <div className="space-y-3">
          <p className="text-sm text-red-300" data-testid="live-detection-error">
            {error}
          </p>
          <button type="button" className="btn-secondary text-sm" onClick={onRefresh}>
            Retry
          </button>
        </div>
      ) : readiness && stats ? (
        <>
          {needsRepair ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3"
              data-testid="live-detection-failed"
            >
              <AlertTriangle className="h-5 w-5 text-red-300 shrink-0" aria-hidden />
              <p className="text-sm text-red-100">Twitch alerts need attention.</p>
            </div>
          ) : isProvisioning ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3"
              data-testid="live-detection-provisioning"
            >
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0" aria-hidden />
              <p className="text-sm text-amber-100">Setting up Twitch alerts...</p>
            </div>
          ) : isReady ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3"
              data-testid="live-detection-ready"
            >
              <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" aria-hidden />
              <p className="text-sm text-emerald-100">
                Live detection is automatic for connected members.
              </p>
            </div>
          ) : null}

          <dl className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-secondary-500">Connected to Twitch</dt>
              <dd className="text-secondary-100">{stats.active_members_with_twitch}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Ready for live detection</dt>
              <dd className="text-secondary-100">{stats.members_with_enabled_pair}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Missing online alerts</dt>
              <dd className="text-secondary-100">{stats.members_missing_online}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Missing offline alerts</dt>
              <dd className="text-secondary-100">{stats.members_missing_offline}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Pending verification</dt>
              <dd className="text-secondary-100">{stats.members_pending_verification}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Failed / revoked</dt>
              <dd className="text-secondary-100">{stats.members_failed}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Missing Twitch</dt>
              <dd className="text-secondary-100">{readiness.members_missing_twitch_count}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Last synced</dt>
              <dd className="text-secondary-100" data-testid="live-detection-last-synced">
                {formatSyncTimestamp(lastSyncedAt)}
              </dd>
            </div>
          </dl>

          {syncMessage ? (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                syncPartialFailure
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
                  : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
              }`}
              data-testid="live-detection-sync-message"
            >
              {syncMessage}
            </div>
          ) : null}

          {lastResult && lastResult.summary.subscriptions_failed > 0 ? (
            <div className="space-y-2" data-testid="live-detection-partial-failure">
              <p className="text-sm font-medium text-red-200">Failed members</p>
              <ul className="text-sm text-red-100/90 list-disc list-inside space-y-1">
                {lastResult.items
                  .filter((item) => item.action === 'failed')
                  .map((item, index) => (
                    <li key={`${item.member_id}-${item.event_type}-${index}`}>
                      {item.twitch_login || item.member_id || 'Member'} · {item.event_type}:{' '}
                      {item.error || 'Sync failed'}
                    </li>
                  ))}
              </ul>
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={onRetrySync}
                data-testid="live-detection-retry"
              >
                Retry sync
              </button>
            </div>
          ) : null}

          <div className="space-y-2 border-t border-secondary-700/60 pt-3">
            <p className="text-xs text-secondary-500" data-testid="live-detection-troubleshooting-note">
              Troubleshooting only: live detection is automatic. Use repair if Twitch alerts failed
              or look stuck.
            </p>
            {canRepairAlerts ? (
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={onOpenSync}
                data-testid="live-detection-sync-button"
              >
                Repair Twitch alerts
              </button>
            ) : (
              <p className="text-sm text-secondary-400" data-testid="live-detection-owner-only">
                Twitch alert repair is available to the league owner.
              </p>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}

const LiveDetectionPanel: React.FC<LiveDetectionPanelProps> = ({
  leagueId,
  readiness,
  readinessLoading,
  onRefreshReadiness,
  canRepairAlerts = true,
  onMutationSuccess,
}) => {
  const [subscriptions, setSubscriptions] = useState<LscLiveSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncPartialFailure, setSyncPartialFailure] = useState(false);
  const [lastResult, setLastResult] = useState<LscLiveSubscriptionSyncResult | null>(null);

  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await getLscLiveSubscriptions(leagueId);
      setSubscriptions(next);
    } catch {
      setError('Live detection status unavailable.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadSubscriptions();
  }, [loadSubscriptions]);

  const handleRefresh = () => {
    void loadSubscriptions();
    void onRefreshReadiness();
  };

  const handleSyncComplete = async (result: LscLiveSubscriptionSyncResult) => {
    setLastResult(result);
    const partial = result.summary.subscriptions_failed > 0;
    setSyncPartialFailure(partial);
    setSyncMessage(formatSyncResultMessage(result.summary, result.items));
    await Promise.all([loadSubscriptions(), Promise.resolve(onRefreshReadiness())]);
    onMutationSuccess?.();
  };

  return (
    <>
      <LiveDetectionPanelView
        readiness={readiness}
        subscriptions={subscriptions}
        loading={loading || readinessLoading}
        error={error}
        syncMessage={syncMessage}
        syncPartialFailure={syncPartialFailure}
        lastResult={lastResult}
        onRefresh={handleRefresh}
        onOpenSync={() => setModalOpen(true)}
        onRetrySync={() => setModalOpen(true)}
        canRepairAlerts={canRepairAlerts}
      />
      {canRepairAlerts ? (
        <LiveDetectionSyncModal
          open={modalOpen}
          leagueId={leagueId}
          readiness={readiness}
          onClose={() => setModalOpen(false)}
          onSyncComplete={(result) => {
            void handleSyncComplete(result);
          }}
        />
      ) : null}
    </>
  );
};

export default LiveDetectionPanel;
